/**
 * Fila de INTENÇÕES da rota ligada ao navegador: localStorage, navigator.onLine e a API.
 * Sem conexão, nada é emitido: a venda fica guardada neste aparelho com uma chave única.
 * Com conexão, é enviada com a MESMA chave — se a resposta se perder e o envio se repetir,
 * o servidor devolve o resultado já gravado (nunca duas vendas ou duas notas). O servidor
 * confere tudo de novo antes de emitir. A lógica da fila está em ./offline-queue.
 */
import type { RouteSaleInput, RouteSaleResult } from "@cafegestor/shared";
import { ApiRequestError, apiFetch, isAppSessionError } from "../api";
import {
  STORE_KEY,
  createIntentQueue,
  type KeyValueStorage,
  type RouteIntent,
} from "./offline-queue";

export type { IntentState, RouteIntent } from "./offline-queue";

/**
 * Erros que não mudam ao repetir (dados ou permissão): param a intenção para revisão.
 * A1: login expirado (401) ou sessão de aplicativo encerrada/sem acesso ao app NÃO são
 * definitivos — a venda continua guardada para o MESMO dono e só é enviada depois de nova
 * autenticação e autorização válidas (nada se perde, nada é enviado por outro usuário).
 */
export function isDefinitiveSendError(err: unknown): boolean {
  return (
    err instanceof ApiRequestError &&
    !isAppSessionError(err) &&
    err.status >= 400 &&
    err.status < 500 &&
    err.code !== "OPERACAO_EM_ANDAMENTO" &&
    err.status !== 401 &&
    err.status !== 408 &&
    err.status !== 429
  );
}

let owner: string | null = null;

/** Onde a fila fica guardada. Padrão: o localStorage do navegador (ERP Web). */
let intentStorage: () => KeyValueStorage | null = () =>
  typeof localStorage === "undefined" ? null : localStorage;

/**
 * O app que hospeda a rota escolhe onde guardar as vendas (V1: o CaféGestor Vendedor usa o
 * armazenamento seguro do Android, e não o localStorage). Recarrega a fila do novo lugar.
 */
export function configureIntentStorage(storage: () => KeyValueStorage | null) {
  intentStorage = storage;
  queue.reload();
}

const queue = createIntentQueue({
  storage: () => intentStorage(),
  owner: () => owner,
  send: (intent) =>
    apiFetch<RouteSaleResult>("/fiscal/rota/vendas", {
      method: "POST",
      body: intent.body,
      idempotencyKey: intent.key,
    }),
  isDefinitive: isDefinitiveSendError,
  isOnline: () => typeof navigator === "undefined" || navigator.onLine,
  newKey: () => crypto.randomUUID(),
  now: () => new Date().toISOString(),
});

/** Dono das intenções: usuário (sessão guardada no aparelho) e empresa ativa. */
export function setIntentOwner(userId: string | null, companyId: string | null) {
  const next = userId && companyId ? `${userId}|${companyId}` : null;
  if (next === owner) return;
  owner = next;
  queue.ownerChanged();
  if (owner) void queue.flush();
}

/** Prefixo das cópias locais das listas da rota (por empresa). */
export const ROUTE_CACHE_PREFIX = "cafegestor.rota.cache.";

/**
 * Saída do usuário: apaga as cópias das listas (clientes, produtos...) deste aparelho e as
 * vendas já concluídas. Vendas ainda não enviadas ficam guardadas para o mesmo usuário e
 * empresa (ninguém mais as vê ou envia).
 */
export function clearRouteDeviceData() {
  owner = null;
  queue.forgetCompleted();
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(ROUTE_CACHE_PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* sem armazenamento */
  }
}

/** Snapshot estável (mesma referência enquanto nada mudar), para useSyncExternalStore. */
export const loadIntents = queue.list;
export const subscribeIntents = queue.subscribe;
export const addIntent = (label: string, body: RouteSaleInput) => queue.add(label, body);
export const removeIntent = queue.remove;
export const flushIntents = queue.flush;
export const sendIntent = async (intent: RouteIntent): Promise<RouteIntent> =>
  (await queue.send(intent.key)) ?? intent;

/** Liga o reenvio automático quando a conexão volta (e acompanha outras abas). */
export function startAutoFlush(): () => void {
  const onOnline = () => void queue.flush();
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORE_KEY) queue.reload();
  };
  window.addEventListener("online", onOnline);
  window.addEventListener("storage", onStorage);
  void queue.flush();
  return () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("storage", onStorage);
  };
}
